import {DocumentNode, gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import React from 'react';

import {SavingState} from 'constants/RegistryConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {FieldTable, FieldTableLoading} from 'registry/field/FieldTable';
import {ImplementingSchemaField} from 'schemas/schema-interface/AddSchemaInterfaceModal';
import {schemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {capwords} from 'util/NameUtil';

import {AddSchemaInterfaceModalFieldTableSchemaFieldSelectors} from './__generated/AddSchemaInterfaceModalFieldTableSchemaFieldSelectors';
import AddSchemaInterfaceModalFieldTableRow from './AddSchemaInterfaceModalFieldTableRow';
export interface AddSchemaInterfaceModalFieldTableProps {
  ownerId: string;
  ownerUrlId: string;
  fields: Array<ImplementingSchemaField>;
  title: string; // Unfortunately `fields` is appended to the title. There are also some other locations that will use the title.
  titleTooltip?: string;
  dataTestAttrs?: DataTestAttrs;
}

export const ADD_SCHEMA_INTERFACE_MODAL_FIELD_TABLE_SCHEMA_FIELD_SELECTORS_QUERY: DocumentNode = gql`
  query AddSchemaInterfaceModalFieldTableSchemaFieldSelectors @skipAudit @engComponent(name: SCHEMAS) {
    schemaFieldSelectors {
      ... on SchemaFieldSelectorNode {
        ...SchemaFieldSelectorGraphQL
        registry {
          id
          organization {
            id
          }
        }
      }
    }
  }
  ${getFragmentWrapper('SchemaFieldSelector').fragment}
`;

function AddSchemaInterfaceModalFieldTable(props: AddSchemaInterfaceModalFieldTableProps): JSX.Element {
  const result = useBenchlingQuery<AddSchemaInterfaceModalFieldTableSchemaFieldSelectors>(
    ADD_SCHEMA_INTERFACE_MODAL_FIELD_TABLE_SCHEMA_FIELD_SELECTORS_QUERY
  );

  if (result.type === QueryResultType.PENDING) {
    return <FieldTableLoading schemaType={capwords(`${schemaFieldsetName()} managed`)} />;
  } else if (result.type === QueryResultType.ERROR) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Error loading schema dropdowns. Try refreshing the page."
      />
    );
  }

  return (
    <FieldTable
      ownerId={props.ownerId}
      ownerUrlId={props.ownerUrlId}
      isBeingCreated={false}
      isDisabled={true}
      shouldShowIsParentLink={true}
      shouldShowIsRequired={true}
      fields={props.fields}
      itemClass={AddSchemaInterfaceModalFieldTableRow}
      title={props.title}
      titleTooltip={props.titleTooltip}
      schemaType={capwords(`${schemaFieldsetName()} managed`)}
      schemaId={null}
      shouldShowTooltipText={true}
      savingState={SavingState.COMPLETED}
      onFieldsChange={noop}
      schemaFieldSelectors={result.type === QueryResultType.SUCCESS ? result.data.schemaFieldSelectors : []}
      schemaInterfaces={[]}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}

export default AddSchemaInterfaceModalFieldTable;
